import { inject, Injectable } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'

import { TranslateService } from '@ngx-translate/core'
import { ReportDialogComponent, ReportDialogData, ReportDialogType } from '../components'

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  private readonly dialog = inject(MatDialog)
  private readonly translateService = inject(TranslateService)

  reportError(action: string, error: any, afterClose?: () => void): string {
    return this.realReportError(ReportDialogType.error, action, error, afterClose)
  }

  reportWarning(action: string, error: any, afterClose?: () => void): string {
    return this.realReportError(ReportDialogType.warning, action, error, afterClose)
  }

  reportMessage(action: string, message: string | string[], buttonLabel: string, afterClose?: () => void): void {
    const messages = Array.isArray(message) ? message : [message]
    const reportDialogData: ReportDialogData = {
      reportDialogType: ReportDialogType.message,
      action,
      messages,
      buttonLabel,
      afterClose,
    }
    this.dialog.open(ReportDialogComponent, {
      data: reportDialogData,
    })
  }

  getErrorMessage(error: any): string {
    if (!error) {
      return 'Unknown error'
    }
    if (typeof error === 'string') {
      return error
    }
    if (error.error) {
      if (typeof error.error === 'string') {
        try {
          const errorObject = JSON.parse(error.error)
          if (errorObject.message) {
            return errorObject.message
          }
        } catch (e) {
          return error.error
        }
      }
      if (error.error.message) {
        return error.error.message
      }
    }
    if (error.message) {
      return error.message
    }
    if (error.statusText) {
      return error.statusText
    }
    if (error.code) {
      return this.translateService.instant('errors.' + error.code, { explanation: error.explanation })
      // return error.code + ': ' + error.explanation
    }
    return 'Unknown error'
  }

  private realReportError(
    reportDialogType: ReportDialogType,
    action: string,
    error: any,
    afterClose?: () => void,
  ): string {
    let errorObject: any = ''
    const errorMessages = this.getErrorMessages(error)
    const reportDialogData: ReportDialogData = {
      reportDialogType,
      action: this.translateService.instant(action),
      messages: errorMessages,
    }
    if (typeof error === 'function') {
      reportDialogData.afterClose = error
    } else {
      if (typeof afterClose === 'function') {
        reportDialogData.afterClose = afterClose
      }
      if (error) {
        errorObject = error
      }
    }
    const errorMessage = errorMessages.join('\n')
    console.error(action, errorMessage, errorObject)
    this.dialog.open(ReportDialogComponent, {
      data: reportDialogData,
    })
    return errorMessage
  }

  private getErrorMessages(error: any): string[] {
    if (Array.isArray(error)) {
      return error.map((e) => this.getErrorMessage(e))
    }
    if (Array.isArray(error?.error?.message)) {
      // @ts-ignore
      return error.error.message.map((e) => this.getErrorMessage(e))
    }
    return [this.getErrorMessage(error)]
  }
}
